/**
 * MarketSuggestion — Phase 9 + Phase 11 dual-source suggestion chip.
 *
 * Hits `/api/market/cross-validate` (RentCast + ATTOM in one round-trip)
 * instead of the single-source `/market/value` or `/market/rent`. Renders
 * the BLENDED midpoint with an agreement-band dot (green/amber/orange/red).
 *
 * Gracefully degrades:
 *   - 1 source resolves → renders that single value with "(1 source)" tag
 *   - 0 sources resolve → renders nothing
 *   - country !== US → hidden
 *   - rate-limited → tiny note (RentCast budget exhausted)
 */
import { useEffect, useState } from "react";
import { Lightbulb, X } from "lucide-react";
import api from "@/lib/api";

function fmtCurrency(n) {
  if (typeof n !== "number" || !isFinite(n)) return "—";
  return `$${Math.round(n).toLocaleString()}`;
}

const AGREEMENT_DOT = {
  strong: { color: "bg-emerald-500", label: "Sources agree" },
  moderate: { color: "bg-amber-500", label: "Sources mostly agree" },
  weak: { color: "bg-orange-500", label: "Sources differ" },
  diverged: { color: "bg-red-500", label: "Sources disagree" },
  single_source: { color: "bg-slate-400", label: "1 source" },
};

function buildAddress(address, city, state, zip) {
  // Helper used by parents to construct the address tail. We keep the chip
  // input contract simple: one `address` string.
  const parts = [address, city, [state, zip].filter(Boolean).join(" ")].filter(Boolean);
  return parts.join(", ").trim().replace(/^,\s*/, "");
}

export default function MarketSuggestion({
  kind,                // "value" | "rent"
  address,
  city,
  state,
  zip,
  country,
  beds,
  baths,
  sqft,
  propertyType,
  enabled = true,
  onAccept,            // (midValue) => void
  testidPrefix = "market",
}) {
  const [payload, setPayload] = useState(null);
  const [rateLimited, setRateLimited] = useState(false);
  const [hidden, setHidden] = useState(false);
  const [loading, setLoading] = useState(false);

  // Reset dismissal whenever the inputs change meaningfully.
  const key = `${kind}|${address}|${city}|${state}|${zip}|${country}|${beds}|${baths}|${sqft}|${propertyType}`;
  useEffect(() => {
    setHidden(false);
  }, [key]);

  // Debounced fetch.
  useEffect(() => {
    if (!enabled) return;
    if (!address || address.length < 6) {
      setPayload(null);
      return;
    }
    if ((country || "").toUpperCase() !== "US") {
      setPayload(null);
      return;
    }
    const handle = setTimeout(async () => {
      setLoading(true);
      try {
        const params = { address, country: country || "US" };
        if (city) params.city = city;
        if (state) params.state = state;
        if (zip) params.zip = zip;
        if (beds) params.beds = beds;
        if (baths) params.baths = baths;
        if (sqft) params.sqft = sqft;
        if (propertyType) params.property_type = propertyType;
        const { data: r } = await api.get("/market/cross-validate", { params });
        if (!r || r.supported === false) {
          setPayload(null);
          setRateLimited(false);
        } else {
          setPayload(r);
          setRateLimited(false);
        }
      } catch (e) {
        setPayload(null);
        setRateLimited(false);
      } finally {
        setLoading(false);
      }
    }, 1000);
    return () => clearTimeout(handle);
  }, [key, enabled, address, city, state, zip, country, beds, baths, sqft, propertyType, kind]);

  if (hidden) return null;
  if (loading && !payload) return null;
  if (!payload && !rateLimited) return null;
  if (rateLimited) {
    return (
      <div
        data-testid={`${testidPrefix}-rate-limited`}
        className="mt-2 inline-flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800"
      >
        Market data unavailable — RentCast monthly limit reached.
      </div>
    );
  }

  // Pick the right field per kind.
  const blob = kind === "rent" ? payload.rent_estimate : payload.value_estimate;
  if (!blob) return null;
  const mid = kind === "rent" ? blob.rent : blob.price;
  if (!mid) return null;
  const low = blob.low;
  const high = blob.high;
  const sources = blob.sources || [];
  const agreement = payload.cross_validation?.agreement || (sources.length === 1 ? "single_source" : "strong");
  const dot = AGREEMENT_DOT[agreement] || AGREEMENT_DOT.single_source;

  const label = kind === "rent" ? "market rent" : "AVM";
  const suffix = kind === "rent" ? "/mo" : "";
  const headline = `${fmtCurrency(mid)}${suffix}`;
  const rangeText = low && high ? `range ${fmtCurrency(low)}–${fmtCurrency(high)}${suffix}` : "";
  const sourceText =
    sources.length === 2
      ? "RentCast + ATTOM"
      : sources[0] === "rentcast"
      ? "RentCast (1 source)"
      : sources[0] === "attom"
      ? "ATTOM (1 source)"
      : "";

  return (
    <div
      data-testid={`${testidPrefix}-suggestion`}
      className="mt-2 flex flex-wrap items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-900"
      title={dot.label}
    >
      <Lightbulb className="h-3.5 w-3.5 text-emerald-600" />
      <span className="flex items-center gap-1.5">
        <span className={`inline-block h-1.5 w-1.5 rounded-full ${dot.color}`} aria-hidden="true" />
        <strong>{sourceText || "Market"} {label}:</strong> {headline}
        {rangeText ? <span className="text-emerald-700"> ({rangeText})</span> : null}
      </span>
      <button
        type="button"
        onClick={() => onAccept?.(Math.round(mid))}
        data-testid={`${testidPrefix}-use`}
        className="ml-auto inline-flex items-center gap-1 rounded-md bg-emerald-600 px-2 py-1 text-[11px] font-medium text-white hover:bg-emerald-700"
      >
        Use this
      </button>
      <button
        type="button"
        onClick={() => setHidden(true)}
        aria-label="Dismiss"
        data-testid={`${testidPrefix}-dismiss`}
        className="rounded-md p-1 text-emerald-700 hover:bg-emerald-100"
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}

export { buildAddress };
